import { useCallback, useEffect, useRef, useState } from 'react';

const PERSIST_DEBOUNCE_MS = 200;

const safeRead = <T>(key: string, fallback: T): T => {
  try {
    const raw = window.sessionStorage.getItem(key);
    if (raw == null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
};

const safeWrite = (key: string, value: unknown): void => {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* sessionStorage full or unavailable; persistence is best-effort */
  }
};

export const usePersistedState = <T>(
  key: string,
  initial: T,
): readonly [T, (updater: (prev: T) => T) => void, () => void] => {
  const [state, setState] = useState<T>(() => safeRead(key, initial));
  const timer = useRef<number | null>(null);

  const set = useCallback(
    (updater: (prev: T) => T): void => {
      setState(prev => {
        const next = updater(prev);
        if (timer.current != null) window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => {
          safeWrite(key, next);
        }, PERSIST_DEBOUNCE_MS);
        return next;
      });
    },
    [key],
  );

  const clear = useCallback((): void => {
    if (timer.current != null) window.clearTimeout(timer.current);
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      /* ignore */
    }
    setState(initial);
  }, [key, initial]);

  useEffect(
    () => (): void => {
      if (timer.current != null) window.clearTimeout(timer.current);
    },
    [],
  );

  return [state, set, clear] as const;
};
